import { useCallback, useEffect, useRef, useState } from 'react';
import { safeStorage } from './playerLogic';

// O descanso entre séries. Conta-se por INSTANTES (quando acaba), não por "menos um por segundo": um telemóvel que adormece o separador
// ou bloqueia o ecrã continua a dizer o tempo certo quando se volta. Sobrevive a recarregar a página (fica guardado por treino).

export const TICK_MS = 250;

/** Vibra, se o aparelho deixar. Nunca rebenta. */
export const vibrate = (pattern) => {
  try { if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') return navigator.vibrate(pattern); } catch { /* sem vibração */ }
  return false;
};

/** Notificação do sistema, só se o sócio já deu licença (nunca se pede sozinha) e a app não está à vista. */
export const notifyRestEnd = (title, body) => {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return false;
    // eslint-disable-next-line no-new
    new Notification(title, { body, tag: 'gymag-rest', silent: false });
    return true;
  } catch { return false; }
};

/** Pede licença para notificar — só a partir de um toque do sócio. */
export const requestNotificationPermission = async () => {
  try {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission !== 'default') return Notification.permission;
    return await Notification.requestPermission();
  } catch { return 'denied'; }
};

/**
 * O estado do descanso: { active, total, remaining, paused, ended }.
 *  - start(seconds)  começa (ou recomeça) um descanso
 *  - pause() / resume()
 *  - add(seconds)    acrescenta (ou tira, se negativo) tempo
 *  - finish()        termina já, sem alerta (o sócio decidiu seguir)
 * Quando chega a zero sozinho: vibra, notifica e fica `ended` até alguém o fechar com finish().
 */
export default function useRestTimer(storageKey, { onEnd } = {}) {
  const [state, setState] = useState(() => {
    const saved = storageKey ? safeStorage.get(`rest.${storageKey}`) : null;
    return saved && (saved.endsAt || saved.pausedRemaining != null) ? saved : { endsAt: null, pausedRemaining: null, total: 0 };
  });
  const [now, setNow] = useState(() => Date.now());
  const firedFor = useRef(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const persist = useCallback((next) => {
    if (storageKey) {
      if (next.endsAt || next.pausedRemaining != null) safeStorage.set(`rest.${storageKey}`, next); else safeStorage.remove(`rest.${storageKey}`);
    }
    return next;
  }, [storageKey]);

  const running = !!state.endsAt;
  const paused = state.pausedRemaining != null;
  const active = running || paused;

  // O relógio só anda enquanto há um descanso a correr.
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [running, state.endsAt]);

  // O telemóvel acordou: recalcula logo (os temporizadores de um separador escondido atrasam-se).
  useEffect(() => {
    const onVisible = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const remainingMs = paused ? state.pausedRemaining : running ? Math.max(0, state.endsAt - now) : 0;
  const ended = running && state.endsAt - now <= 0;

  useEffect(() => {
    if (!ended || firedFor.current === state.endsAt) return;
    firedFor.current = state.endsAt;
    vibrate([220, 120, 220, 120, 420]);
    notifyRestEnd('Descanso terminado', 'Hora da próxima série.');
    if (onEndRef.current) onEndRef.current();
  }, [ended, state.endsAt]);

  const start = useCallback((seconds) => {
    const s = Math.max(1, Math.round(Number(seconds) || 0));
    firedFor.current = null;
    setNow(Date.now());
    setState(persist({ endsAt: Date.now() + s * 1000, pausedRemaining: null, total: s }));
  }, [persist]);

  const pause = useCallback(() => setState((cur) => {
    if (!cur.endsAt) return cur;
    return persist({ ...cur, endsAt: null, pausedRemaining: Math.max(0, cur.endsAt - Date.now()) });
  }), [persist]);

  const resume = useCallback(() => {
    setNow(Date.now());   // o relógio mostra já a hora certa, não a de antes da pausa
    setState((cur) => {
      if (cur.pausedRemaining == null) return cur;
      firedFor.current = null;
      return persist({ ...cur, endsAt: Date.now() + cur.pausedRemaining, pausedRemaining: null });
    });
  }, [persist]);

  const add = useCallback((seconds) => {
    setNow(Date.now());
    setState((cur) => {
      const delta = Math.round(Number(seconds) || 0) * 1000;
      if (cur.endsAt) { firedFor.current = null; return persist({ ...cur, endsAt: Math.max(Date.now() + 1000, cur.endsAt + delta), total: Math.max(1, cur.total + delta / 1000) }); }
      if (cur.pausedRemaining != null) return persist({ ...cur, pausedRemaining: Math.max(1000, cur.pausedRemaining + delta), total: Math.max(1, cur.total + delta / 1000) });
      return cur;
    });
  }, [persist]);

  const finish = useCallback(() => {
    firedFor.current = null;
    setState(persist({ endsAt: null, pausedRemaining: null, total: 0 }));
  }, [persist]);

  return {
    active,
    paused,
    ended,
    total: state.total,
    remaining: Math.ceil(remainingMs / 1000),
    fraction: state.total > 0 ? Math.min(1, Math.max(0, 1 - remainingMs / (state.total * 1000))) : 0,
    start, pause, resume, add, finish,
  };
}
